import {TaggedNodeGraph} from './TaggedNodeGraph';

/**
 * The possible display modes of nodes and links in the visualization.
 * Either all nodes and links are default, or some set of nodes and links are highlighted
 * and the rest are dehighlighted.
 */
export enum HighlightMode {
  HIGHLIGHTED = 'HIGHLIGHTED',
  DEFAULT = 'DEFAULT',
  DEHIGHLIGHTED = 'DEHIGHLIGHTED',
}

export function getNodeHighlightMode(
  nodeId: string,
  {
    clickedUpstreamNodeId,
    clickedDownstreamNodeId,
    taggedNodeGraph,
  }: {
    clickedUpstreamNodeId?: string | null;
    clickedDownstreamNodeId?: string | null;
    taggedNodeGraph: TaggedNodeGraph;
  }
): HighlightMode {
  // We might have an invalid clicked node due to node deletion or retaining a selection from
  // another graph, so treat it as a null selection in that case.
  const isUpstreamNodeValid =
    clickedUpstreamNodeId != null && taggedNodeGraph.isValidNodeId(clickedUpstreamNodeId);
  const isDownstreamNodeValid =
    clickedDownstreamNodeId != null && taggedNodeGraph.isValidNodeId(clickedDownstreamNodeId);
  if (!isUpstreamNodeValid && !isDownstreamNodeValid) {
    return HighlightMode.DEFAULT;
  }
  if (isUpstreamNodeValid && taggedNodeGraph.isDescendant(clickedUpstreamNodeId, nodeId)) {
    return HighlightMode.HIGHLIGHTED;
  } else if (isDownstreamNodeValid && taggedNodeGraph.isDescendant(nodeId, clickedDownstreamNodeId)) {
    return HighlightMode.HIGHLIGHTED;
  } else {
    return HighlightMode.DEHIGHLIGHTED;
  }
}

/**
 * Either parentNodeId or childNodeId may be null, representing the cases of an initial link
 * or terminal link, respectively.
 */
export function getLinkHighlightMode(
  parentNodeId: string | null,
  childNodeId: string | null,
  {
    clickedUpstreamNodeId,
    clickedDownstreamNodeId,
    taggedNodeGraph,
  }: {
    clickedUpstreamNodeId?: string | null;
    clickedDownstreamNodeId?: string | null;
    taggedNodeGraph: TaggedNodeGraph;
  }
): HighlightMode {
  // We might have an invalid clicked node due to node deletion or retaining a selection from
  // another graph, so treat it as a null selection in that case.
  const isUpstreamNodeValid =
    clickedUpstreamNodeId != null && taggedNodeGraph.isValidNodeId(clickedUpstreamNodeId);
  const isDownstreamNodeValid =
    clickedDownstreamNodeId != null && taggedNodeGraph.isValidNodeId(clickedDownstreamNodeId);
  if (!isUpstreamNodeValid && !isDownstreamNodeValid) {
    return HighlightMode.DEFAULT;
  }
  if (
    isUpstreamNodeValid &&
    childNodeId &&
    taggedNodeGraph.isDescendant(clickedUpstreamNodeId, childNodeId)
  ) {
    return HighlightMode.HIGHLIGHTED;
  } else if (
    isDownstreamNodeValid &&
    parentNodeId &&
    taggedNodeGraph.isDescendant(parentNodeId, clickedDownstreamNodeId)
  ) {
    return HighlightMode.HIGHLIGHTED;
  } else if (parentNodeId === clickedUpstreamNodeId && childNodeId === clickedDownstreamNodeId) {
    return HighlightMode.HIGHLIGHTED;
  } else {
    return HighlightMode.DEHIGHLIGHTED;
  }
}
